
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { AlertTriangle, Gauge, RotateCcw, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { toggleBikeMovementBlock, setBikeSpeedLimit, resetBikeMovementRestrictions } from '@/pages/admin/bikes/services/movementControlApi';
import { format } from 'date-fns';

interface MovementControlPanelProps {
  bikeId: string;
  currentBlocked?: boolean;
  currentSpeedLimit?: number | null;
  maxAllowedSpeed?: number;
  lastMovementUpdate?: string | null;
  onUpdate?: () => Promise<void>;
  loading?: boolean;
}

export const MovementControlPanel: React.FC<MovementControlPanelProps> = ({
  bikeId,
  currentBlocked = false,
  currentSpeedLimit,
  maxAllowedSpeed = 25,
  lastMovementUpdate,
  onUpdate,
  loading = false
}) => {
  const [isBlocked, setIsBlocked] = useState(currentBlocked);
  const [speedLimit, setSpeedLimit] = useState<string>(currentSpeedLimit?.toString() || '');
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleBlockToggle = async (checked: boolean) => {
    if (checked) {
      setShowBlockConfirm(true);
    } else {
      setIsUpdating(true);
      try {
        const result = await toggleBikeMovementBlock(bikeId, false);
        if (result.success) {
          setIsBlocked(false);
          await onUpdate?.();
        }
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const handleConfirmBlock = async () => {
    setIsUpdating(true);
    try {
      const result = await toggleBikeMovementBlock(bikeId, true);
      if (result.success) {
        setIsBlocked(true);
        setSpeedLimit(''); // Сбрасываем ограничение скорости при блокировке
        await onUpdate?.();
      }
    } finally {
      setIsUpdating(false);
      setShowBlockConfirm(false);
    }
  };

  const handleSpeedLimitApply = async () => {
    const limit = speedLimit ? parseInt(speedLimit) : null;
    
    if (limit && (limit < 1 || limit > maxAllowedSpeed)) {
      toast.error(`Скорость должна быть от 1 до ${maxAllowedSpeed} км/ч`);
      return;
    }

    setIsUpdating(true);
    try {
      const result = await setBikeSpeedLimit(bikeId, limit);
      if (result.success) {
        setIsBlocked(false); // При установке ограничения снимаем полную блокировку
        await onUpdate?.();
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReset = async () => {
    setIsUpdating(true);
    try {
      const result = await resetBikeMovementRestrictions(bikeId);
      if (result.success) {
        setIsBlocked(false);
        setSpeedLimit('');
        await onUpdate?.();
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const isDisabled = loading || isUpdating;
  
  // Форматируем время последнего обновления
  const formattedLastUpdate = lastMovementUpdate ? 
    format(new Date(lastMovementUpdate), 'dd.MM.yyyy HH:mm:ss') : 
    'Никогда';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-5 w-5" />
          Управление движением
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Блокировка движения */}
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="movement-block">Полная блокировка движения</Label>
            <p className="text-sm text-muted-foreground">Полностью останавливает велосипед</p>
          </div>
          <Switch
            id="movement-block"
            checked={isBlocked}
            onCheckedChange={handleBlockToggle}
            disabled={isDisabled}
          />
        </div>

        {/* Ограничение скорости */}
        <div className="space-y-2">
          <Label htmlFor="speed-limit">Ограничение скорости (км/ч)</Label>
          <div className="flex gap-2">
            <Input
              id="speed-limit"
              type="number"
              min="1"
              max={maxAllowedSpeed}
              value={speedLimit}
              onChange={(e) => setSpeedLimit(e.target.value)}
              placeholder={`Макс. ${maxAllowedSpeed}`}
              disabled={isDisabled || isBlocked}
            />
            <Button 
              onClick={handleSpeedLimitApply}
              disabled={isDisabled || isBlocked}
              size="sm"
            >
              {isUpdating ? 'Применяется...' : 'Применить'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Оставьте пустым для снятия ограничения
          </p>
        </div>

        {/* Кнопка сброса */}
        <Button 
          variant="outline" 
          onClick={handleReset}
          disabled={isDisabled}
          className="w-full"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          {isUpdating ? 'Сброс...' : 'Сбросить все ограничения'}
        </Button>

        {/* Диалог подтверждения блокировки */}
        <AlertDialog open={showBlockConfirm} onOpenChange={setShowBlockConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Подтвердите блокировку
              </AlertDialogTitle>
              <AlertDialogDescription>
                Вы уверены, что хотите полностью заблокировать движение велосипеда? 
                Это действие немедленно остановит велосипед, если он в данный момент используется.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isUpdating}>Отмена</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmBlock}
                disabled={isUpdating}
                className="bg-destructive hover:bg-destructive/90"
              >
                {isUpdating ? 'Блокируется...' : 'Заблокировать'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
      <CardFooter className="flex items-center pt-2 text-xs text-muted-foreground border-t">
        <Clock className="h-3 w-3 mr-1" />
        Последнее обновление: {formattedLastUpdate}
      </CardFooter>
    </Card>
  );
};
